import { useEffect, useMemo, useState } from "react";
import MultiWorkLogModal from "./MultiWorkLogModal";
import UpdateGroupModal from "./UpdateGroupModal";
import {
  fetchProjectsListSimple,
  fetchTaskPlanners,
  saveDailyWorkLogBulk,
  saveDailyWorkplan,
  updateDailyWorkplan,
} from "./ddApi";
import { todayStr } from "./worklogHelpers";
import "./taskPlanner.css";

const formatUtcTime = (value) => {
  if (!value) return "N/A";
  return new Date(value).toLocaleTimeString(undefined, {
    timeZone: "UTC",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatGroupDate = (dateStr) => {
  if (dateStr === "Unscheduled") return "Unscheduled Tasks";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const buildComparisonRows = (planners = [], timeLogs = [], date = "") => {
  const isToday = date === todayStr();
  const rows = [];
  const logsBySubactivity = {};
  timeLogs.forEach((log) => {
    const key = log.subactivity_id;
    if (!logsBySubactivity[key]) logsBySubactivity[key] = [];
    logsBySubactivity[key].push(log);
  });
  const matchedLogIds = new Set();
  planners.forEach((planner) => {
    const candidates = logsBySubactivity[planner.subactivity_id] || [];
    const matchedLog = candidates.find((l) => !matchedLogIds.has(l.id)) || null;
    if (matchedLog) matchedLogIds.add(matchedLog.id);
    rows.push({
      type: matchedLog ? "completed" : isToday ? "pending" : "not_done",
      planner,
      worklog: matchedLog,
    });
  });
  timeLogs.forEach((log) => {
    if (!matchedLogIds.has(log.id)) rows.push({ type: "extra", planner: null, worklog: log });
  });
  return rows;
};

const STATUS = {
  completed: { label: "Completed", pill: "completed", dot: "#1a7f37" },
  pending: { label: "Pending", pill: "pending", dot: "#d4a72c" },
  not_done: { label: "Not Done", pill: "not_done", dot: "#cf222e" },
  extra: { label: "Unplanned Work", pill: "extra", dot: "#8250df" },
};

const EmployeeComparisonCard = ({ employee, date }) => {
  const [collapsed, setCollapsed] = useState(false);
  const rows = useMemo(
    () => buildComparisonRows(employee.planners || [], employee.time_logs || [], date),
    [employee, date]
  );
  const counts = useMemo(
    () => ({
      completed: rows.filter((r) => r.type === "completed").length,
      pending: rows.filter((r) => r.type === "pending").length,
      extra: rows.filter((r) => r.type === "extra").length,
    }),
    [rows]
  );

  if (rows.length === 0) {
    return (
      <div className="tp-card" style={{ padding: 16 }}>
        <strong>{employee.emp_name}</strong>
        <p className="tp-subtitle">{employee.emp_code} · No planners or logs</p>
      </div>
    );
  }

  return (
    <div className="tp-card">
      <button type="button" className="tp-card-head" onClick={() => setCollapsed((c) => !c)}>
        <div>
          <h3>{employee.emp_name}</h3>
          <p>{employee.emp_code}</p>
        </div>
        <div className="tp-pills">
          {counts.completed > 0 && <span className="tp-pill completed">{counts.completed} completed</span>}
          {counts.pending > 0 && <span className="tp-pill pending">{counts.pending} pending</span>}
          {counts.extra > 0 && <span className="tp-pill extra">{counts.extra} unplanned</span>}
          <span>{collapsed ? "▸" : "▾"}</span>
        </div>
      </button>
      {!collapsed && (
        <div style={{ overflowX: "auto" }}>
          <table className="tp-table">
            <thead>
              <tr>
                <th />
                <th>Project</th>
                <th>Activity / Subactivity</th>
                <th>Planned</th>
                <th>Actual log</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const cfg = STATUS[row.type];
                const p = row.planner;
                const w = row.worklog;
                return (
                  <tr key={idx}>
                    <td><span className="tp-dot" style={{ background: cfg.dot }} /></td>
                    <td>
                      <strong>{p?.project_name || w?.project_name || "—"}</strong>
                      <div className="tp-subtitle">{p?.project_code || w?.project_code || ""}</div>
                    </td>
                    <td>
                      <div>{p?.activity_name || w?.activity_name || "—"}</div>
                      <div className="tp-subtitle">{p?.subactivity_name || w?.subactivity_name || "—"}</div>
                    </td>
                    <td>
                      {p ? (
                        <>
                          <div>{formatUtcTime(p.start_time)} to {formatUtcTime(p.end_time)}</div>
                          <div className="tp-subtitle">{p.duration || "—"}</div>
                          {p.note && <div className="tp-note">{p.note}</div>}
                        </>
                      ) : (
                        <span className="tp-muted">Not planned</span>
                      )}
                    </td>
                    <td>
                      {w ? (
                        <>
                          <div>{formatUtcTime(w.start_time)} – {formatUtcTime(w.end_time)}</div>
                          <div className="tp-subtitle">{w.duration || "—"}</div>
                          {w.note && <div className="tp-note">{w.note}</div>}
                        </>
                      ) : (
                        <span className="tp-muted">No log</span>
                      )}
                    </td>
                    <td>
                      <span className={`tp-status tp-pill ${cfg.pill}`}>{cfg.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MyTasksTable = ({ group }) => (
  <div style={{ overflowX: "auto" }}>
    <table className="tp-table">
      <thead>
        <tr>
          <th>Task Note</th>
          <th>Project Code</th>
          <th>Project</th>
          <th>Subactivity</th>
          <th>Schedule</th>
          <th>Duration</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {group.tasks.map((task) => {
          const isCompleted = task.status !== "not_done";
          return (
            <tr key={task.id}>
              <td>
                <strong>{task.note || "Untitled task"}</strong>
              </td>
              <td>
                <span className="tp-code">{task?.project_detail?.project_code || task.project_code || "—"}</span>
              </td>
              <td>{task?.project_detail?.project_name || task.project_name || "Unnamed project"}</td>
              <td>
                <div>{task.subactivity_detail?.subactivity_name || task.subactivity_name || "—"}</div>
                <div className="tp-subtitle">{task.activity_detail?.activity_name || task.activity_name || ""}</div>
              </td>
              <td>
                <div>{formatUtcTime(task.start_time)}</div>
                <div className="tp-subtitle">to {formatUtcTime(task.end_time)}</div>
              </td>
              <td>{task.duration || "00:00:00"}</td>
              <td>
                <span className={`tp-pill ${isCompleted ? "completed" : "pending"}`}>
                  {isCompleted ? "Completed" : "Pending"}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const TaskPlanner = () => {
  const role = sessionStorage.getItem("sitemanagement_role") || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [startDate, setStartDate] = useState(todayStr());
  const [showMultiLog, setShowMultiLog] = useState(false);
  const [updateModalData, setUpdateModalData] = useState({ isOpen: false, date: "", tasks: [] });
  const [taskPlanners, setTaskPlanners] = useState([]);
  const [taskPlannersData, setTaskPlannersData] = useState(null);
  const [projectsListAll, setProjectsListAll] = useState([]);

  const availableTabs = useMemo(() => {
    if (["ACCOUNT", "ADMIN", "TL", "BD", "CertificationAccess", "CertificationAccessBd"].includes(role)) {
      return ["My Tasks", "User Tasks"];
    }
    return ["My Tasks"];
  }, [role]);

  const [activeTab, setActiveTab] = useState("My Tasks");
  const isComparisonTab = activeTab === "User Tasks" || activeTab === "TL Tasks";

  const load = async () => {
    setRefreshing(true);
    try {
      const [plannerRes, projects] = await Promise.all([
        fetchTaskPlanners({ activeTab, date: startDate || todayStr() }),
        fetchProjectsListSimple().catch(() => []),
      ]);
      setTaskPlanners(plannerRes.planners || []);
      setTaskPlannersData(plannerRes.data || null);
      setProjectsListAll(Array.isArray(projects) ? projects : []);
    } catch {
      setTaskPlanners([]);
      setTaskPlannersData(null);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, [activeTab, startDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredPlanners = useMemo(() => {
    const arr = Array.isArray(taskPlanners) ? taskPlanners : [];
    return arr.filter((p) => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (p.note || "").toLowerCase().includes(q) || (p.project_name || "").toLowerCase().includes(q);
    });
  }, [taskPlanners, searchTerm]);

  const groupedPlanners = useMemo(() => {
    const groups = {};
    filteredPlanners.forEach((p) => {
      const key = p.date ? String(p.date).split("T")[0] : "Unscheduled";
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return Object.keys(groups)
      .sort((a, b) => {
        if (a === "Unscheduled") return 1;
        if (b === "Unscheduled") return -1;
        return new Date(b) - new Date(a);
      })
      .map((key) => ({
        date: key,
        tasks: groups[key].sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || ""))),
      }));
  }, [filteredPlanners]);

  const comparisonEmployees = useMemo(() => {
    if (!isComparisonTab) return [];
    return taskPlannersData?.employees || [];
  }, [isComparisonTab, taskPlannersData]);

  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return comparisonEmployees;
    const q = searchTerm.toLowerCase();
    return comparisonEmployees.filter(
      (emp) => (emp.emp_name || "").toLowerCase().includes(q) || (emp.emp_code || "").toLowerCase().includes(q)
    );
  }, [comparisonEmployees, searchTerm]);

  const comparisonSummary = taskPlannersData?.summary || null;
  const todaysGroup = groupedPlanners.find((g) => g.date === todayStr());
  const isEdit = (() => {
    if (!todaysGroup?.tasks?.length) return false;
    const first = [...todaysGroup.tasks].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
    if (!first?.created_at) return false;
    return (new Date() - new Date(first.created_at)) / 60000 <= 60;
  })();

  return (
    <div className="tp-page attendance-history-cont">
      <div className="tp-header">
        <div className="tp-header-copy">
          <span className="tp-page-icon" aria-hidden="true">⏱</span>
          <div>
            <h1 className="tp-title">
              Task Planner
              <span className="tp-count">
                {isComparisonTab ? `${filteredEmployees.length} employees` : `${filteredPlanners.length} tasks`}
              </span>
            </h1>
            <p className="tp-subtitle">
              {isComparisonTab
                ? "Planner vs worklog — see what was planned and what was actually done"
                : "Plan your day and log hours against project sub-activities"}
            </p>
          </div>
        </div>
        <div className="tp-header-actions">
          <button type="button" className="bg-icon-btn bg-icon-btn--outline" onClick={load} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {!isComparisonTab && (
            todaysGroup ? (
              <button
                type="button"
                className="bg-icon-btn"
                onClick={() => setUpdateModalData({ isOpen: true, date: todaysGroup.date, tasks: todaysGroup.tasks })}
              >
                Update today's plan
              </button>
            ) : (
              <button type="button" className="bg-icon-btn" onClick={() => setShowMultiLog(true)}>
                Plan task
              </button>
            )
          )}
        </div>
      </div>

      {availableTabs.length > 1 && (
        <div className="tp-tabs">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`tp-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      <div className="tp-filterbar">
        <input
          type="text"
          placeholder={isComparisonTab ? "Search by employee name or code..." : "Search by note or project..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        {startDate && (
          <button type="button" className="bg-icon-btn bg-icon-btn--outline" onClick={() => setStartDate(todayStr())}>
            Today
          </button>
        )}
      </div>

      {isComparisonTab && (
        <div className="tp-legend">
          <span><span className="tp-legend-dot" style={{ background: "#1a7f37" }} /> Completed — planned & logged</span>
          <span><span className="tp-legend-dot" style={{ background: "#d4a72c" }} /> Pending — planned, not logged</span>
          <span><span className="tp-legend-dot" style={{ background: "#8250df" }} /> Extra work — logged without a plan</span>
        </div>
      )}

      {refreshing ? (
        <div className="tp-empty">
          <div className="tp-empty-icon">⏳</div>
          <p>Loading task planner…</p>
        </div>
      ) : isComparisonTab ? (
        filteredEmployees.length === 0 ? (
          <div className="tp-empty">
            <div className="tp-empty-icon">👥</div>
            <p>No comparison data</p>
            <p className="tp-subtitle">No employees with plans for the selected date.</p>
          </div>
        ) : (
          <>
            {comparisonSummary && (
              <div className="tp-summary">
                {[
                  ["Employees", comparisonSummary.total_employees],
                  ["Total planners", comparisonSummary.total_planners],
                  ["Completed", comparisonSummary.completed_planners],
                  ["Pending", comparisonSummary.pending_planners],
                  ["Work logs", comparisonSummary.total_time_logs],
                ].map(([label, value]) => (
                  <div key={label} className="tp-summary-card">
                    <strong>{value ?? 0}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}
            {filteredEmployees.map((emp) => (
              <EmployeeComparisonCard key={emp.emp_code || emp.emp_name} employee={emp} date={startDate || todayStr()} />
            ))}
          </>
        )
      ) : groupedPlanners.length === 0 ? (
        <div className="tp-empty">
          <div className="tp-empty-icon">🗓</div>
          <p>No tasks found</p>
          <p className="tp-subtitle">Try another date, or start a new plan for today.</p>
          <button type="button" className="bg-icon-btn" onClick={() => setShowMultiLog(true)}>
            Plan task
          </button>
        </div>
      ) : (
        groupedPlanners.map((group) => (
          <div key={group.date} className="tp-card">
            <div className="tp-card-head" style={{ cursor: "default" }}>
              <h3>{formatGroupDate(group.date)}</h3>
              <span className="tp-pill pending">{group.tasks.length} {group.tasks.length === 1 ? "task" : "tasks"}</span>
            </div>
            <MyTasksTable group={group} />
          </div>
        ))
      )}

      <MultiWorkLogModal
        isOpen={showMultiLog}
        onClose={() => setShowMultiLog(false)}
        projects={projectsListAll}
        onSave={async (date, rows) => {
          const payload = rows.map((r) => ({ ...r, date, status: r.status || "WORKED" }));
          await saveDailyWorkplan(payload);
          setShowMultiLog(false);
          load();
        }}
      />

      <UpdateGroupModal
        isOpen={updateModalData.isOpen}
        onClose={() => setUpdateModalData({ ...updateModalData, isOpen: false })}
        projects={projectsListAll}
        isEdit={isEdit}
        initialData={updateModalData}
        onSave={async (date, rows) => {
          const payload = rows.map((r) => ({ ...r, date, status: r.status || "WORKED" }));
          await updateDailyWorkplan(payload);
          load();
        }}
        onSaveWorklog={async (date, rows) => {
          const payload = rows.map((r) => ({ ...r, date, status: r.status || "WORKED" }));
          await saveDailyWorkLogBulk(payload);
          load();
        }}
      />
    </div>
  );
};

export default TaskPlanner;
